import { createAsyncThunk } from '@reduxjs/toolkit';

import { getDictionary } from '@travel/i18n';
import loggerGenerator from '@travel/logger';

import { defaultImageUrl, getImageUrl } from 'constants/providerReview';
import paths from 'core/universalRouter/paths';
import { selectIsRedirectError } from 'store/commonError/selectors';
import { getIsViewingOtherUser, getProfileById } from 'store/profileById/selector';
import { selectProviderHeaderInfo } from 'store/providerCommonHeader/selectors';
import { getUserProfile } from 'store/userProfile/selectors';
import { MY_REVIEW_PAGE, PROVIDER_REVIEW_PAGE, REVIEW_POST_PAGE } from 'utils/ratHelper';
import { getJoinedTranslation } from 'utils/translationUtil';

import { DEFAULT_REVIEW_HOST } from 'constants/common';
import { updateSeoData } from './slice';

import type { SeoData } from './types';
export const fetchSEOData = createAsyncThunk<
  SeoData,
  { pageName: string; providerId?: string },
  { dispatch: any; getState: any }
>('fetch/SEOData', async ({ pageName, providerId }, { dispatch, getState }) => {
  const state = getState();
  const dictionary = getDictionary(state);

  const isError = selectIsRedirectError(state);
  const isViewingOtherUser = getIsViewingOtherUser(state);
  const profileData = isViewingOtherUser ? getProfileById(state) : getUserProfile(state);
  const nickName =
    profileData?.nickname || getJoinedTranslation('Review_SEO.Review_profile.Reviewer', dictionary);
  const userId = profileData?.userId;
  const profileImageUrl = profileData?.profileAvatar?.url || defaultImageUrl;
  const ogUrl = isViewingOtherUser
    ? `${DEFAULT_REVIEW_HOST}${paths.otherUserReviewPage.pathResolver(userId)}`
    : `${DEFAULT_REVIEW_HOST}${paths.myReviewPage.path}`;

  const ogImageUrl = getImageUrl(providerId || '');

  const logger = loggerGenerator();

  const siteName = getJoinedTranslation('Review_SEO.Structure_Data.Publisher', dictionary);

  const defaultSeoData = {
    isTwitterTags: false,
  };

  let seoData: Partial<SeoData> = {};

  switch (pageName) {
    case PROVIDER_REVIEW_PAGE:
      const headerInfo = selectProviderHeaderInfo(state);

      const hotelName = headerInfo?.hotel?.hotelName ?? '';

      const description = getJoinedTranslation(
        'Review_SEO.Provider_Review_List.Description',
        dictionary,
        { hotel_name: hotelName },
      );
      const pathUrl = providerId && `${paths.providerReview.canonicalPathResolver(providerId)}`;

      seoData = {
        ...defaultSeoData,
        title: getJoinedTranslation('Review_SEO.Provider_Review_List.Title', dictionary, {
          hotel_name: hotelName,
        }),
        description: description,
        pageTitle: isError
          ? getJoinedTranslation(
              'Review_Common.Error_Message.Infrastruture_Error.Fatal.HTML_Title',
              dictionary,
            )
          : getJoinedTranslation('Review_SEO.Provider_Review_List.Title', dictionary, {
              hotel_name: hotelName,
            }),
        canonicalPath: pathUrl,
        imageUrl: ogImageUrl,
        isIndexablePage: true,
        siteName: siteName,
        type: 'article',
        isDefaultTags: true,
        isDescription: true,
        isTwitterTags: true,
      };
      break;
    case MY_REVIEW_PAGE:
      seoData = {
        ...defaultSeoData,
        title: getJoinedTranslation('Review_SEO.Review_profile.Title', dictionary, {
          users_nickname: nickName,
        }),
        description: getJoinedTranslation('Review_SEO.Review_profile.Description', dictionary, {
          users_nickname: nickName,
        }),
        pageTitle: isError
          ? getJoinedTranslation(
              'Review_Common.Error_Message.Infrastruture_Error.Fatal.HTML_Title',
              dictionary,
            )
          : getJoinedTranslation('Review_SEO.Review_profile.Title', dictionary, {
              users_nickname: nickName,
            }),
        imageUrl: profileImageUrl,
        ogUrl: ogUrl,
        siteName: siteName,
        type: 'profile',
        isIndexablePage: false,
        isTwitterTags: true,
        isDefaultTags: true,
        isDescription: isViewingOtherUser,
      };
      break;
    case REVIEW_POST_PAGE:
      seoData = {
        ...defaultSeoData,
        title: getJoinedTranslation('Review_SEO.Review_Post.Title', dictionary),
        pageTitle: isError
          ? getJoinedTranslation(
              'Review_Common.Error_Message.Infrastruture_Error.Fatal.HTML_Title',
              dictionary,
            )
          : getJoinedTranslation('Review_SEO.Review_Post.Title', dictionary),
        isIndexablePage: false,
        isTwitterTags: false,
        isDefaultTags: false,
      };
      break;

    default:
      logger.error('SEO data is not configured for this page');
      break;
  }

  dispatch(updateSeoData(seoData));

  return seoData as SeoData;
});
